// Skiplist memtable. A real probabilistic skiplist (p=1/4, max height 12),
// not a sorted array with extra steps: inserts splice at every level, reads
// descend from the top. Same key overwrites in place, keeping the newest seq.

import { Entry, cmp } from "./types";

const MAX_HEIGHT = 12;
const P = 0.25;

class Node {
  next: (Node | null)[];
  constructor(
    public key: string,
    public value: string | null,
    public seq: number,
    height: number,
  ) {
    this.next = new Array(height).fill(null);
  }
}

export class Skiplist {
  private head = new Node("", null, -1, MAX_HEIGHT);
  private height = 1;
  private _count = 0;
  private _bytes = 0;

  /** Approximate memory footprint used for the flush threshold. */
  get bytes(): number {
    return this._bytes;
  }
  get count(): number {
    return this._count;
  }

  private randomHeight(): number {
    let h = 1;
    while (h < MAX_HEIGHT && Math.random() < P) h++;
    return h;
  }

  /**
   * Splice a key in at every level of its tower.
   *
   * Algorithm (Pugh 1990): descend from the top level, walking forward while
   * the next key sorts before the target, and remember the last node visited
   * on each level — those are exactly the nodes whose forward pointers must
   * change. Expected O(log n) comparisons.
   *
   * Returns the tower height and comparison count so callers can report the
   * real cost of the insert.
   */
  insert(
    key: string,
    value: string | null,
    seq: number,
  ): { height: number; comparisons: number; overwrote: boolean } {
    const update: Node[] = new Array(MAX_HEIGHT).fill(this.head);
    let x: Node = this.head;
    let comparisons = 0;
    for (let i = this.height - 1; i >= 0; i--) {
      let nxt = x.next[i];
      while (nxt !== null && ++comparisons && cmp(nxt.key, key) < 0) {
        x = nxt;
        nxt = x.next[i];
      }
      update[i] = x;
    }
    const candidate = x.next[0];
    if (candidate !== null && candidate.key === key) {
      // Overwrite: memtable holds only the newest version of a key.
      this._bytes += byteLen(value) - byteLen(candidate.value);
      candidate.value = value;
      candidate.seq = seq;
      return { height: candidate.next.length, comparisons, overwrote: true };
    }
    const h = this.randomHeight();
    if (h > this.height) {
      for (let i = this.height; i < h; i++) update[i] = this.head;
      this.height = h;
    }
    const node = new Node(key, value, seq, h);
    for (let i = 0; i < h; i++) {
      node.next[i] = update[i].next[i];
      update[i].next[i] = node;
    }
    this._count++;
    this._bytes += key.length + byteLen(value) + 16; // 16 ≈ node overhead
    return { height: h, comparisons, overwrote: false };
  }

  /** undefined = key not present; Entry with value:null = tombstone. */
  get(key: string): Entry | undefined {
    return this.probe(key).entry;
  }

  /**
   * Search, reporting the comparison count.
   *
   * Same descent as insert: at each level walk forward while the next key is
   * smaller, then drop a level. Expected O(log_{1/p} n) comparisons — with
   * p=1/4 that is log base 4 of the entry count.
   */
  probe(key: string): { entry: Entry | undefined; comparisons: number } {
    let x: Node = this.head;
    let comparisons = 0;
    for (let i = this.height - 1; i >= 0; i--) {
      let nxt = x.next[i];
      while (nxt !== null && ++comparisons && cmp(nxt.key, key) < 0) {
        x = nxt;
        nxt = x.next[i];
      }
    }
    const n = x.next[0];
    if (n !== null && n.key === key) {
      return { entry: { key: n.key, value: n.value, seq: n.seq }, comparisons };
    }
    return { entry: undefined, comparisons };
  }

  /** Current top level of the tower structure (grows as the list grows). */
  get levels(): number {
    return this.height;
  }

  /** All entries in key order — this is what a flush serializes. */
  *iterate(): IterableIterator<Entry> {
    let n = this.head.next[0];
    while (n !== null) {
      yield { key: n.key, value: n.value, seq: n.seq };
      n = n.next[0];
    }
  }

  /** Entries with key in [lo, hi). */
  *range(lo: string, hi: string): IterableIterator<Entry> {
    let x: Node = this.head;
    for (let i = this.height - 1; i >= 0; i--) {
      let nxt = x.next[i];
      while (nxt !== null && cmp(nxt.key, lo) < 0) {
        x = nxt;
        nxt = x.next[i];
      }
    }
    let n = x.next[0];
    while (n !== null && cmp(n.key, hi) < 0) {
      yield { key: n.key, value: n.value, seq: n.seq };
      n = n.next[0];
    }
  }
}

function byteLen(v: string | null): number {
  return v === null ? 0 : v.length;
}
